import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import type { PMIssueDependency } from "@/domains/pm/types";
import { listIssueDependencies } from "@/stores/pm/repository";

export interface IssueDependencyIndex {
  all: PMIssueDependency[];
  /** Edges pointing INTO an issue — its predecessors. */
  incoming: Map<number, PMIssueDependency[]>;
  /** Edges pointing OUT of an issue — what it drives. */
  outgoing: Map<number, PMIssueDependency[]>;
  /** Whether moving this issue can move or upset anything else. */
  hasDependents: (issueId: number) => boolean;
  hasPredecessors: (issueId: number) => boolean;
}

const EMPTY: PMIssueDependency[] = [];

/**
 * Every dependency among one building's issues, indexed both ways.
 *
 * One query for the building rather than one per issue: the Gantt draws every
 * arrow at once, and the detail panel wants both directions for the issue it
 * is showing.
 */
export function useIssueDependencies(buildingId: number | undefined) {
  const { data, isLoading } = useQuery({
    queryKey: ["pm-issue-dependencies", buildingId],
    queryFn: () => listIssueDependencies(buildingId as number),
    enabled: buildingId !== undefined && Number.isFinite(buildingId),
    staleTime: 30_000,
  });

  const index = useMemo<IssueDependencyIndex>(() => {
    const all = data ?? EMPTY;
    const incoming = new Map<number, PMIssueDependency[]>();
    const outgoing = new Map<number, PMIssueDependency[]>();
    const push = (map: Map<number, PMIssueDependency[]>, key: number, edge: PMIssueDependency) => {
      const existing = map.get(key);
      if (existing) existing.push(edge);
      else map.set(key, [edge]);
    };
    for (const edge of all) {
      push(incoming, edge.successor_issue_id, edge);
      push(outgoing, edge.predecessor_issue_id, edge);
    }
    return {
      all,
      incoming,
      outgoing,
      hasDependents: (issueId: number) => (outgoing.get(issueId)?.length ?? 0) > 0,
      hasPredecessors: (issueId: number) => (incoming.get(issueId)?.length ?? 0) > 0,
    };
  }, [data]);

  return { dependencies: index, isLoading };
}
